import { formatLocalDate, formatLocalDateTime } from '@/lib/formatters';
import { UNKNOWN_ACTIVITY_STYLE, activityTypeConfig, isActivityAction, type ActivityAction, type ActivityStyle } from '@/config/activity';

/** One row of `GET /api/dashboard/activity` (`{ data: AuditLog[] }`, newest first, with `user` included). */
export interface ApiAuditLog {
  id: string;
  firmId: string;
  userId: string | null;
  entity: string;
  entityId: string;
  action: string;
  /** Prisma Json column. Read only by `describeChanges`, which takes its top-level primitive fields. */
  changes: unknown;
  /** Prisma Json column. Not read by the feed. */
  metadata: unknown;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  user: { id: string; name: string; avatar: string | null } | null;
}

export interface ActivityView {
  id: string;
  /** Entity name from the API (e.g. "Task"); used by the section filter. */
  section: string;
  entityId: string;
  /** Known API action, or 'unknown' for an action this build has no style for. */
  type: ActivityAction | 'unknown';
  style: ActivityStyle;
  title: string;
  detail: string;
  user: string;
  userInitials: string;
  /** Local calendar day, used to group rows by date. */
  day: string;
  timestamp: string;
  timeAgo: string;
  sectionIcon: string;
  sectionColor: string;
  sectionBg: string;
  userColor: string;
}

const SYSTEM_USER = 'System';
const SYSTEM_INITIALS = 'SY';

export function dayKey(date: Date): string {
  return formatLocalDate(date);
}

function getTimeAgo(createdMs: number): string {
  const mins = Math.floor((Date.now() - createdMs) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function initialsOf(name: string): string {
  return name.split(' ').map((part) => part[0]).join('').slice(0, 2).toUpperCase();
}

const SUMMARY_MAX_FIELDS = 3;
/** The Activity nav badge counts entries from this window. */
const NEW_ACTIVITY_WINDOW_MS = 24 * 60 * 60 * 1000;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isPrimitive(value: unknown): value is string | number | boolean {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

/**
 * Short summary of a `changes` value: its top-level primitive fields as `key: value`, at most three,
 * then `+N more`. Nested objects, arrays and non-objects have no summary, so they give ''.
 */
function describeChanges(changes: unknown): string {
  if (!isRecord(changes)) return '';
  const fields = Object.entries(changes).flatMap(([key, value]) => (isPrimitive(value) ? [`${key}: ${value}`] : []));
  const shown = fields.slice(0, SUMMARY_MAX_FIELDS);
  const hidden = fields.length - shown.length;
  return [...shown, ...(hidden > 0 ? [`+${hidden} more`] : [])].join(', ');
}

/**
 * Entries from the last 24 hours that are newer than the last visit to Activity (epoch ms; null when never visited).
 * `now` is a parameter so the window can be tested.
 */
export function countNewActivity(entries: readonly ApiAuditLog[], lastVisit: number | null, now: number): number {
  return entries.filter((entry) => {
    const createdAt = Date.parse(entry.createdAt);
    return now - createdAt < NEW_ACTIVITY_WINDOW_MS && (lastVisit === null || createdAt > lastVisit);
  }).length;
}

export function toActivityView(item: ApiAuditLog): ActivityView {
  const created = new Date(item.createdAt);
  const known = isActivityAction(item.action) ? item.action : null;
  const style = known ? activityTypeConfig[known] : UNKNOWN_ACTIVITY_STYLE;
  const userName = item.user?.name ?? SYSTEM_USER;
  return {
    id: item.id,
    section: item.entity,
    entityId: item.entityId,
    type: known ?? 'unknown',
    style,
    title: `${item.entity} ${known ? style.label.toLowerCase() : item.action}`,
    detail: describeChanges(item.changes),
    user: userName,
    userInitials: item.user ? initialsOf(item.user.name) : SYSTEM_INITIALS,
    day: dayKey(created),
    timestamp: formatLocalDateTime(created),
    timeAgo: getTimeAgo(created.getTime()),
    sectionIcon: 'ri-file-line',
    sectionColor: 'text-teal-400',
    sectionBg: 'bg-teal-500/15',
    userColor: 'bg-teal-500',
  };
}
